import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  CollectionPreferences,
  CollectionPreferencesProps,
} from '@cloudscape-design/components';
import { getFromLocalStorage, saveToLocalStorage } from 'src/utils/localStorageUtils';

// Define our own column definition interface
export interface ColumnDefinition {
  id: string;
  label: string;
  editable?: boolean;
}

export interface TablePreferencesRef {
  refreshColumns: () => void;
}

export interface TablePreferencesProps<
  TItem = any,
  TColumn extends { id: string; header: string } = any,
> {
  /**
   * Unique identifier for the table to store preferences separately
   */
  tableId: string;

  /**
   * Default visible columns (if no preferences are saved)
   */
  defaultVisibleColumns?: string[];

  /**
   * Default page size (if no preferences are saved)
   */
  defaultPageSize?: number;

  /**
   * Available page sizes
   */
  pageSizeOptions?: number[];

  /**
   * All column definitions for the table
   */
  allColumnDefinitions: TColumn[];

  /**
   * All items for the table
   */
  allItems: TItem[];

  /**
   * Callback when filtered items or columns change
   */
  onPreferencesChange: (data: {
    visibleColumns: TColumn[];
    paginatedItems: TItem[];
    currentPage: number;
    pageSize: number;
  }) => void;
}

/**
 * A reusable component for table preferences that handles column visibility and page size
 * and persists settings to localStorage
 */
const TablePreferences = forwardRef<TablePreferencesRef, TablePreferencesProps>(
  (
    {
      tableId,
      defaultPageSize = 10,
      pageSizeOptions = [10, 20, 50, 100],
      allColumnDefinitions,
      allItems,
      onPreferencesChange,
    }: TablePreferencesProps,
    ref: React.Ref<TablePreferencesRef>,
  ) => {
    // Storage keys
    const columnsStorageKey = `${tableId}-columns-preferences`;
    const pageSizeStorageKey = `${tableId}-page-size-preference`;

    const columnPreferences: ColumnDefinition[] = allColumnDefinitions.map((col) => ({
      id: col.id,
      label: col.header,
      editable: true,
    }));

    // Default visible columns if not provided
    const initialVisibleColumns = columnPreferences.map((col) => col.id);
    const selectedColumns =
      getFromLocalStorage<string[]>(columnsStorageKey, initialVisibleColumns) ||
      initialVisibleColumns;
    // Get saved preferences from localStorage or use defaults
    const [preferences, setPreferences] = useState({
      visibleColumns: selectedColumns,
      pageSize:
        getFromLocalStorage<number>(pageSizeStorageKey, defaultPageSize) ||
        defaultPageSize,
    });

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);

    // Memoized computed values to prevent infinite loops
    const visibleColumns = useMemo(
      () => allColumnDefinitions.filter((col) => selectedColumns?.includes(col.id)),
      [allColumnDefinitions, selectedColumns],
    );

    const paginatedItems = useMemo(() => {
      const startIndex = (currentPage - 1) * preferences.pageSize;
      return allItems.slice(startIndex, startIndex + preferences.pageSize);
    }, [allItems, currentPage, preferences.pageSize]);

    // Use ref to track previous values and prevent infinite loops
    const prevValuesRef = useRef<string>();

    // Expose refreshColumns method to parent component
    const refreshColumns = useCallback(() => {
      onPreferencesChange({
        visibleColumns,
        paginatedItems,
        currentPage,
        pageSize: preferences.pageSize,
      });
    }, [
      visibleColumns,
      paginatedItems,
      currentPage,
      preferences.pageSize,
      onPreferencesChange,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        refreshColumns,
      }),
      [refreshColumns],
    );

    // Apply preferences when dependencies change
    useEffect(() => {
      const currentValues = JSON.stringify({
        visibleColumns: visibleColumns.map((col) => col.id),
        itemsLength: allItems.length,
        currentPage,
        pageSize: preferences.pageSize,
      });

      if (prevValuesRef.current !== currentValues) {
        prevValuesRef.current = currentValues;
        onPreferencesChange({
          visibleColumns,
          paginatedItems,
          currentPage,
          pageSize: preferences.pageSize,
        });
      }
    }, [
      allColumnDefinitions,
      allItems,
      visibleColumns,
      paginatedItems,
      currentPage,
      preferences.pageSize,
    ]);

    // Handle preferences change
    const handlePreferencesChange: CollectionPreferencesProps['onConfirm'] = ({
      detail,
    }) => {
      const newPreferences = {
        visibleColumns: [...(detail.visibleContent || initialVisibleColumns)],
        pageSize: detail.pageSize || defaultPageSize,
      };

      // Save to state
      setPreferences(newPreferences);
      setCurrentPage(1); // Reset to first page when preferences change

      // Save to localStorage
      saveToLocalStorage(columnsStorageKey, newPreferences.visibleColumns);
      saveToLocalStorage(pageSizeStorageKey, newPreferences.pageSize);

      // Update parent with new preferences
      const newVisibleColumns = allColumnDefinitions.filter((col) =>
        newPreferences.visibleColumns.includes(col.id),
      );
      const newPaginatedItems = allItems.slice(0, newPreferences.pageSize);

      onPreferencesChange({
        visibleColumns: newVisibleColumns,
        paginatedItems: newPaginatedItems,
        currentPage: 1,
        pageSize: newPreferences.pageSize,
      });
    };

    return (
      <CollectionPreferences
        title="Preferences"
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        preferences={{
          pageSize: preferences.pageSize,
          visibleContent: preferences.visibleColumns,
        }}
        pageSizePreference={{
          title: 'Page size',
          options: pageSizeOptions.map((option) => ({
            value: option,
            label: `${option} items`,
          })),
        }}
        visibleContentPreference={{
          title: 'Column preferences',
          options: [
            {
              label: 'Table columns',
              options: columnPreferences.map((column) => ({
                id: column.id,
                label: column.label,
                editable: column.editable !== false,
              })),
            },
          ],
        }}
        onConfirm={handlePreferencesChange}
      />
    );
  },
);

TablePreferences.displayName = 'TablePreferences';

export default TablePreferences;
